import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { ArrowLeftRight, Building2, ChevronDown, LifeBuoy, LogOut, ShieldCheck, User, UserCog, X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useAssumedRole } from '@/contexts/AssumedRoleContext'
import { useTenant } from '@/contexts/TenantContext'
import { SwitchRoleModal } from '@/components/SwitchRoleModal'
import { useBugReportEnabled } from '@/features/bug-report/useBugReportEnabled'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'

import { transformUserRole } from '@/utils/transformers'
import { AppExperienceMenu } from './AppExperienceMenu'

export interface AppUserProfileProps {
  /** Render the inline "Viewing as …" chip (default: true). */
  showAssumedRoleIndicator?: boolean
}

export function AppUserProfile({ showAssumedRoleIndicator = true }: AppUserProfileProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser, logout } = useAuth()
  const { assumedRole, canSwitchRoles, clearAssumedRole } = useAssumedRole()
  // The tenant the user belongs to — homeTenant, not currentTenant, so a
  // platform admin viewing another tenant still sees their own membership.
  const { homeTenant, currentTenant } = useTenant()
  const userTenant = homeTenant ?? currentTenant
  const { enabled: bugReportEnabled } = useBugReportEnabled()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showSwitchRoleModal, setShowSwitchRoleModal] = useState(false)
  const closeUserMenu = useCallback(() => setShowUserMenu(false), [])


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      // Keep the menu open while the Bugsnap button or dialog is interacted with,
      // so the screenshot can capture the page with the dropdown still visible.
      if (isBugsnapUiEvent(target)) return
      if (!target.closest('.user-profile-container')) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  useEffect(() => {
    return () => {
      setShowUserMenu(false)
    }
  }, [location.pathname])

  const handleLogout = async () => {
    setShowUserMenu(false)
    await logout()
  }

  if (!currentUser) {
    return null
  }

  const hasRoles = Boolean(currentUser.roles && currentUser.roles.length > 0)

  return (
    <>
      {showAssumedRoleIndicator && assumedRole && (
        <div className="assumed-role-indicator">
          <ShieldCheck size={13} />
          <span>
            Viewing as <strong>{assumedRole.name}</strong>
          </span>
          <button
            type="button"
            className="assumed-role-indicator__exit"
            onClick={() => {
              clearAssumedRole()
            }}
            title="Exit role preview"
          >
            <X size={12} />
          </button>
        </div>
      )}

      <div className="user-profile-container">
        <button
          type="button"
          className="user-profile-btn"
          onClick={() => setShowUserMenu(!showUserMenu)}
          title={t('nav.userMenu')}
          aria-label={t('nav.userMenu')}
          aria-haspopup="menu"
          aria-expanded={showUserMenu}
        >
          <div className="user-avatar">
            <User size={16} />
          </div>
          <span className="user-name">{currentUser.display_name}</span>
          <ChevronDown
            size={14}
            className={`user-menu-icon${showUserMenu ? ' rotated' : ''}`}
          />
        </button>
        {showUserMenu && (
          <div className="user-menu" role="menu">
            {(userTenant || hasRoles || assumedRole) && (
              <div className="user-menu-role-section">
                {userTenant && (
                  <div
                    className="user-menu-tenant"
                    title={`${t('nav.tenant')}: ${userTenant.name}`}
                  >
                    <Building2 size={12} className="user-menu-tenant__icon" />
                    <span className="user-menu-tenant__name">{userTenant.name}</span>
                  </div>
                )}
                {(hasRoles || assumedRole) && (
                  <div className="user-menu-role-section__chips">
                    {hasRoles && (
                      <div className="user-menu-role-chip">
                        {currentUser.is_platform_admin ? 'PLATFORM ADMIN' : transformUserRole(currentUser.roles, false)}
                      </div>
                    )}
                    {assumedRole && (
                      <div className="user-menu-role-chip user-menu-role-chip--assumed">
                        <ShieldCheck size={10} />
                        {assumedRole.name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {canSwitchRoles && (
              <button
                type="button"
                className="user-menu-item user-menu-item--switch-role"
                role="menuitem"
                onClick={() => {
                  setShowUserMenu(false)
                  setShowSwitchRoleModal(true)
                }}
              >
                <ArrowLeftRight size={14} />
                <span>{t('nav.switchRole')}</span>
              </button>
            )}
            <button
              type="button"
              className="user-menu-item"
              role="menuitem"
              onClick={() => {
                setShowUserMenu(false)
                navigate('/settings')
              }}
            >
              <UserCog size={14} />
              <span>{t('nav.account')}</span>
            </button>
            <AppExperienceMenu close={closeUserMenu} />
            <button
              type="button"
              className="user-menu-item user-menu-item--danger"
              role="menuitem"
              onClick={() => void handleLogout()}
            >
              <LogOut size={14} />
              <span>{t('nav.logout')}</span>
            </button>
            {bugReportEnabled && (
              <button
                type="button"
                className="user-menu-item"
                role="menuitem"
                onClick={() => {
                  setShowUserMenu(false)
                  window.open('/help/bug-reports', '_blank')
                }}
              >
                <LifeBuoy size={14} />
                <span>{t('nav.helpBugReportsTitle', 'Bugsnap Dashboard')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      <SwitchRoleModal isOpen={showSwitchRoleModal} onClose={() => setShowSwitchRoleModal(false)} />

    </>
  )
}
